import React, { useState, useEffect } from 'react';
import { Link, BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import authService from './shared/utils/authService';
import Login from './features/auth/Login';
import Signup from './features/auth/Signup';
import ExpenseTable from './features/expenses/ExpenseTable';
import PaymentSourcePieChart from './features/expenses/PaymentSourcePieChart';
import MonthlySummaryTable from './features/expenses/MonthlySummaryTable';
import PaymentSourceMonthlyBarChart from './features/expenses/PaymentSourceMonthlyBarChart';
import CategoryCostChart from './features/expenses/CategoryCostChart';
import MonthlyStackedBarChart from './features/expenses/MonthlyStackedBarChart';
import CategorySubcategoryMonthlyCharts from './features/expenses/CategorySubcategoryMonthlyCharts';
import BudgetVisualization from './features/expenses/BudgetVisualization';
import 'bootstrap/dist/css/bootstrap.min.css';
import RulesTab from './features/expenses/RulesTab';
import ManualTransactionEntry from './features/expenses/ManualTransactionEntry';
import HomePage from './features/home/HomePage';
import { Tab, Tabs, Row, Col, Alert, Button, Card } from 'react-bootstrap';
import LifeManagementDashboard from './features/life/LifeManagementDashboard';
import StockMarketDashboard from './features/stocks/StockMarketDashboard';
import { LockFill, PlusCircle, ArrowUpCircleFill, ArrowDownCircleFill, WalletFill, JournalText, PiggyBank, HeartPulse, ChevronBarRight, ChevronBarLeft } from 'react-bootstrap-icons';
import { formatCurrency } from './shared/utils/currency';
import SideBar from './shared/components/SideBar';
import AssetDashboard from './features/assets/AssetDashboard';
import RecordAsset from './features/assets/RecordAsset';
import AssetTypeManager from './features/assets/AssetTypeManager';
import SubscriptionModal from './shared/components/SubscriptionModal';
import AdminPanel from './features/admin/AdminPanel';
import AppHeader from './shared/components/AppHeader'; // Import AppHeader

import Settings from './features/settings/Settings';
import StockDetail from './features/stocks/StockDetail';
import { DEFAULT_EXCLUDED_CATEGORIES } from './shared/utils/excludedCategories';

// Centralized API URL configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
axios.defaults.baseURL = API_BASE_URL;

const ActivityType = {
    TAB_HOME_CLICKED: "TAB_HOME_CLICKED",
    TAB_PROFILE_DASHBOARD_CLICKED: "TAB_PROFILE_DASHBOARD_CLICKED",
    TAB_RECORD_TRANSACTION_CLICKED: "TAB_RECORD_TRANSACTION_CLICKED",
    TAB_RECORD_ASSET_CLICKED: "TAB_RECORD_ASSET_CLICKED",
    TAB_RULES_CLICKED: "TAB_RULES_CLICKED",
    TAB_CLICKED: "TAB_CLICKED",
    SUBTAB_OVERVIEW_CLICKED: "SUBTAB_OVERVIEW_CLICKED",
    SUBTAB_SUBCATEGORY_TRENDS_CLICKED: "SUBTAB_SUBCATEGORY_TRENDS_CLICKED",
    SUBTAB_TRANSACTION_DETAILS_CLICKED: "SUBTAB_TRANSACTION_DETAILS_CLICKED",
    SUBTAB_BUDGET_CLICKED: "SUBTAB_BUDGET_CLICKED",
};

// Setup axios interceptor
axios.interceptors.request.use(config => {
    const requestUrl = config.url || '';
    if (requestUrl.includes('/api/auth/')) {
        return config;
    }
    const user = authService.getCurrentUser();
    if (user && user.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
});

axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response && error.response.status === 401) {
            authService.logout();
        }
        return Promise.reject(error);
    }
);

const PrivateRoute = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(!!authService.getCurrentUser());

    useEffect(() => {
        const handleAuthChange = () => {
            setIsLoggedIn(!!authService.getCurrentUser());
        };

        window.addEventListener('storage', handleAuthChange);
        const intervalId = setInterval(handleAuthChange, 1000); // Check every second

        return () => {
            window.removeEventListener('storage', handleAuthChange);
            clearInterval(intervalId);
        };
    }, []);

    return isLoggedIn ? children : <Navigate to="/login" />;
};

function MainApp({ currentUser, onSubscribe }) {
    const [income, setIncome] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [monthlyAggregatedExpenses, setMonthlyAggregatedExpenses] = useState([]);
    const [selectedPaymentSourceForChart, setSelectedPaymentSourceForChart] = useState("");
    const [selectedPaymentSourceForMonthlyTable, setSelectedPaymentSourceForMonthlyTable] = useState("");
    const [settings, setSettings] = useState({ categories: [], rules: [], budgets: [], currency: 'USD' });
    const [categoryCostData, setCategoryCostData] = useState([]);
    const [monthlyCategoryExpenses, setMonthlyCategoryExpenses] = useState([]);
    const [key, setKey] = useState('home'); // Set initial active tab to 'home'
    const [dashboardSubTabKey, setDashboardSubTabKey] = useState('overview');
    const [paymentSources, setPaymentSources] = useState([]);
    const [excludedCategories, setExcludedCategories] = useState(DEFAULT_EXCLUDED_CATEGORIES);
    const [allTransactions, setAllTransactions] = useState([]);
    const [assets, setAssets] = useState([]); // New state for assets
    const [assetTypes, setAssetTypes] = useState([]); // New state for asset types
    const [selectedYears, setSelectedYears] = useState([new Date().getFullYear().toString()]);
    const handleSelectedYearsChange = (e) => {
        const selected = Array.from(e.target.selectedOptions, (option) => option.value);
        // Never allow zero years selected - fall back to whatever was
        // already chosen rather than leaving every year-scoped chart empty.
        if (selected.length > 0) {
            setSelectedYears(selected);
        }
    };
    const [profiles, setProfiles] = useState([]);
    const [activeProfileId, setActiveProfileId] = useState(localStorage.getItem('activeProfileId'));
    const [profileTypeLoaded, setProfileTypeLoaded] = useState(false); // New state for profile type loaded status
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [showCreateProfileModalFromHome, setShowCreateProfileModalFromHome] = useState(false); // New state for modal from HomePage
    const [showSidebar, setShowSidebar] = useState(false);
    const navigate = useNavigate();

    const logActivity = async (activityType, details = {}, profileId = null) => {
        try {
            const token = localStorage.getItem('token');
            const safeActivityType = activityType || ActivityType.TAB_CLICKED;
            await axios.post(`${API_BASE_URL}/api/admin/log_activity`, { activity_type: safeActivityType, profile_id: profileId }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
        } catch (error) {
            console.error("Error logging activity:", error);
        }
    };

    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    const normalizePaymentSource = (value) => {
        if (!value || typeof value !== 'string') return value;
        const trimmed = value.trim();
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] && typeof parsed[0] === 'object') {
                return parsed[0].source_name || parsed[0].value || parsed[0].name || parsed[0].label || value;
            }
            if (parsed && typeof parsed === 'object') {
                return parsed.source_name || parsed.value || parsed.name || parsed.label || value;
            }
            return value;
        } catch {
            return value;
        }
    };

    const fetchData = async () => {
        if (activeProfileId) {
            localStorage.setItem('activeProfileId', activeProfileId);
            try {
                const profileResponse = await axios.get(`${API_BASE_URL}/api/profiles/${activeProfileId}`);
                const activeProfileType = profileResponse.data.profile_type;

                if (activeProfileType === "EXPENSE_MANAGER") {
                    const response = await axios.get(`${API_BASE_URL}/api/expenses?profile_id=${activeProfileId}`, { params: { excluded_categories: excludedCategories } });
                    const allTransactionsWithId = [...response.data.income, ...response.data.expenses].map((t, index) => ({
                        ...t,
                        payment_source: normalizePaymentSource(t.payment_source),
                        id: t.id || `${t.date}-${t.description}-${t.amount}-${normalizePaymentSource(t.payment_source)}-${index}`
                    }));
                    setAllTransactions(allTransactionsWithId);
                    
                    // Parse icon JSON strings back to objects
                    const settingsWithParsedIcons = {
                        ...response.data.settings,
                        categories: response.data.settings.categories.map(category => ({
                            ...category,
                            icon: typeof category.icon === 'string' && category.icon.startsWith('{') 
                                ? JSON.parse(category.icon) 
                                : category.icon,
                            subcategories: category.subcategories.map(subcategory => {
                                // Handle both string and object subcategories
                                if (typeof subcategory === 'string') {
                                    return subcategory; // Keep old string format
                                } else if (typeof subcategory === 'object') {
                                    return {
                                        ...subcategory,
                                        icon: typeof subcategory.icon === 'string' && subcategory.icon.startsWith('{')
                                            ? JSON.parse(subcategory.icon)
                                            : subcategory.icon
                                    };
                                }
                                return subcategory;
                            })
                        }))
                    };
                    setSettings(settingsWithParsedIcons);

                    const paymentSourcesResponse = await axios.get(`${API_BASE_URL}/api/payment_sources/profiles/${activeProfileId}/payment_sources`);
                    setPaymentSources(paymentSourcesResponse.data); // Store full objects
                } else if (activeProfileType === "ASSET_MANAGER") {
                    const assetTypesResponse = await axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/asset_types`);
                    setAssetTypes(assetTypesResponse.data);

                    const assetsResponse = await axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/assets`);
                    setAssets(assetsResponse.data);
                    setSettings(prevSettings => ({ ...prevSettings, currency: profileResponse.data.currency }));
                } else if (activeProfileType === "LIFE_MANAGER") {
                    // Life management profile - no additional data needed for now
                } else if (activeProfileType === "MARKET_WATCH") {
                    // Market watch profile - watchlist data will be fetched by the dashboard component
                }
                setProfileTypeLoaded(true); // Set to true after data is fetched
            } catch (error) {
                console.error('Error fetching data: ', error);
                setProfileTypeLoaded(true); // Set to true even on error to avoid infinite loading
            }
        } else {
            setProfileTypeLoaded(true); // Set to true if no activeProfileId
        }
    };

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/profiles`)
            .then(response => {
                const fetchedProfiles = response.data;
                setProfiles(fetchedProfiles);

                // Always start with home page on fresh login
                // Clear any stored profile selection
                localStorage.removeItem('activeProfileId');
                setActiveProfileId(null);
            })
            .catch(error => {
                console.error('Error fetching profiles: ', error);
            });
    }, []);

    useEffect(() => {
        fetchData();
    }, [activeProfileId, key, excludedCategories]);

    useEffect(() => {
    const fetchProfileAndData = async () => {
      if (activeProfileId) {
        try {
          const profileResponse = await axios.get(`${API_BASE_URL}/api/profiles/${activeProfileId}`);
          const activeProfileType = profileResponse.data.profile_type;

          if (activeProfileType === "EXPENSE_MANAGER") {
            const filteredTransactions = allTransactions.filter(t => selectedYears.includes(new Date(t.date).getFullYear().toString()));
            const filteredIncome = filteredTransactions.filter(t => t.amount >= 0);
            const filteredExpenses = filteredTransactions.filter(t => t.amount < 0);

            setIncome(filteredIncome);
            setExpenses(filteredExpenses);

            Promise.all(selectedYears.map(year =>
              axios.get(`${API_BASE_URL}/api/analysis/category_costs?profile_id=${activeProfileId}`, { params: { year, excluded_categories: excludedCategories } })
            ))
              .then(responses => {
                const merged = responses.flatMap(r => Array.isArray(r.data) ? r.data : []);
                setCategoryCostData(merged);
              })
              .catch(error => {
                console.error('Error fetching category cost data: ', error);
              });

            if (dashboardSubTabKey === 'subcategoryTrends' || dashboardSubTabKey === 'overview') {
              Promise.all(selectedYears.map(year =>
                axios.get(`${API_BASE_URL}/api/analysis/monthly_category_expenses?profile_id=${activeProfileId}`, { params: { year, excluded_categories: excludedCategories } })
              ))
                .then(responses => {
                    const allMonthlyData = responses.flatMap(r => Array.isArray(r.data) ? r.data : []);
                    const aggregatedMonthlyExpenses = allMonthlyData.reduce((acc, item) => {
                      const { YearMonth, total_cost } = item;
                      if (!acc[YearMonth]) {
                        acc[YearMonth] = 0;
                      }
                      acc[YearMonth] += total_cost;
                      return acc;
                    }, {});
                    const monthlyAggregatedExpensesArray = Object.keys(aggregatedMonthlyExpenses).map(YearMonth => ({
                      YearMonth,
                      total_cost: aggregatedMonthlyExpenses[YearMonth]
                    }));
                    setMonthlyAggregatedExpenses(monthlyAggregatedExpensesArray);
                    const uniqueMonthYears = [...new Set(allMonthlyData.map(item => item.YearMonth))].sort();
                    const last12MonthYears = uniqueMonthYears.slice(Math.max(uniqueMonthYears.length - 12, 0));
                    const filteredMonthlyData = allMonthlyData.filter(item =>
                      last12MonthYears.includes(item.YearMonth) && !excludedCategories.includes(item.Category)
                    );
                    setMonthlyCategoryExpenses(filteredMonthlyData);
                })
                .catch(error => {
                  console.error('Error fetching monthly category expenses data: ', error);
                });
            } else {
              setMonthlyCategoryExpenses([]);
              setMonthlyAggregatedExpenses([]);
            }
          } else if (activeProfileType === "ASSET_MANAGER") {
            axios.get(`${API_BASE_URL}/api/assets/profiles/${activeProfileId}/assets/summary?year=${[...selectedYears].sort().slice(-1)[0]}`)
              .then(response => {
              })
              .catch(error => {
                console.error('Error fetching asset summary for dashboard:', error);
              });
          } else if (activeProfileType === "LIFE_MANAGER") {
            // Life management dashboard data - will be implemented later
          }
        } catch (error) {
          console.error('Error fetching profile type:', error);
        }
      }
    };

    fetchProfileAndData();
  }, [activeProfileId, selectedYears, allTransactions, excludedCategories, dashboardSubTabKey]);

    const handleCreateProfile = (profileData) => {
        const { publicId, profileName, currency, profileType, primaryPrioritization, primaryTimeManagement } = profileData;
        const payload = { 
            public_id: publicId, 
            name: profileName, 
            currency: currency, 
            profile_type: profileType,
            ...(profileType === 'LIFE_MANAGER' && {
                primary_prioritization: primaryPrioritization,
                primary_time_management: primaryTimeManagement
            })
        };
        
        axios.post(`${API_BASE_URL}/api/profiles`, payload)
            .then(response => {
                setProfiles([...profiles, response.data]);
            })
            .catch(error => {
                console.error('Error creating profile: ', error);
            });
    };

  const handleUpdateProfile = (profileId, newName, newCurrency, newProfileType) => {
    axios.put(`${API_BASE_URL}/api/profiles/${profileId}`, { name: newName, currency: newCurrency, profile_type: newProfileType })
      .then(response => {
        const updatedProfiles = profiles.map(p => p.id === profileId ? response.data : p);
        setProfiles(updatedProfiles);
      })
      .catch(error => {
        console.error('Error updating profile: ', error);
      });
  };

  const handleDeleteProfile = (profileId) => {
    axios.delete(`${API_BASE_URL}/api/profiles/${profileId}`)
      .then(response => {
        const updatedProfiles = profiles.filter(p => p.id !== profileId);
        setProfiles(updatedProfiles);
        if (activeProfileId === profileId) {
          setActiveProfileId(updatedProfiles.length > 0 ? updatedProfiles[0].id : null);
        }
      })
      .catch(error => {
        console.error('Error deleting profile: ', error);
      });
  };

  const handleSaveSettings = (newSettings) => {
    // Convert icon objects to JSON strings for backend compatibility
    const settingsForBackend = {
      ...newSettings,
      categories: newSettings.categories.map(category => ({
        ...category,
        icon: typeof category.icon === 'object' ? JSON.stringify(category.icon) : category.icon,
        subcategories: category.subcategories.map(subcategory => {
          if (typeof subcategory === 'object' && subcategory.icon) {
            return {
              ...subcategory,
              icon: typeof subcategory.icon === 'object' ? JSON.stringify(subcategory.icon) : subcategory.icon
            };
          }
          return subcategory;
        })
      }))
    };
    
    axios.post(`${API_BASE_URL}/api/settings?profile_id=${activeProfileId}`, settingsForBackend)
      .then(response => {
        alert('Settings saved successfully!');
        setSettings(newSettings); // Keep original format in state
        
        // Refresh data to ensure UI shows persisted data
        fetchData();
      })
      .catch(error => {
        console.error('Error saving settings:', error);
      });
  };
  const handleAddRuleFromTransaction = (newRule) => {
    const updatedSettings = {
      ...settings,
      rules: [...settings.rules, newRule]
    };
    handleSaveSettings(updatedSettings);
  };

  const handleUpdateTransactionCategory = (transactionId, newCategoryString) => {
    const [newCategory, newSubcategory] = newCategoryString.split(': ');
    const updatedExpenses = expenses.map(exp =>
      exp.id === transactionId ? { ...exp, category: newCategory, subcategory: newSubcategory } : exp
    );
    const updatedIncome = income.map(inc =>
      inc.id === transactionId ? { ...inc, category: newCategory, subcategory: newSubcategory } : inc
    );
    setExpenses(updatedExpenses);
    setIncome(updatedIncome);

    axios.put(`${API_BASE_URL}/api/transactions/category`, {
      id: transactionId,
      new_category: newCategoryString
    })
    .catch(error => {
      console.error('Error updating transaction category:', error);
    });
  };

  const handleDeleteTransaction = (transactionId) => {
    axios.delete(`${API_BASE_URL}/api/transactions/${transactionId}?profile_id=${activeProfileId}`)
      .then(response => {
        fetchData(); // Refresh data after deletion
      })
      .catch(error => {
        console.error('Error deleting transaction:', error);
      });
  };

  const handleUpdateTransaction = (transactionId, updatedFields) => {
    axios.put(`${API_BASE_URL}/api/transactions/${transactionId}?profile_id=${activeProfileId}`, updatedFields)
      .then(response => {
        fetchData(); // Refresh data after edit
      })
      .catch(error => {
        console.error('Error updating transaction:', error);
        alert(error.response?.data?.detail || error.response?.data?.message || 'Failed to update transaction.');
      });
  };

    const handleProfileVisibilityChange = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/profiles`);
      setProfiles(response.data);

      const updatedActiveProfile = response.data.find(p => p.id === activeProfileId);
      if (updatedActiveProfile && updatedActiveProfile.is_hidden) {
        setActiveProfileId(null);
        setKey('home');
      }
    } catch (error) {
      console.error('Error re-fetching profiles after visibility change:', error);
    }
  };

    const handleSubscribe = (period) => {
    onSubscribe(period).then(() => {
        setShowSubscribeModal(false);
    });
  };

    const PROFILE_SHELL_META = {
        EXPENSE_MANAGER: {
            title: 'Expense Command Center',
            description: 'Analyze spending trends, budgets, and transaction health in one place.',
            icon: JournalText,
            eyebrow: 'Expense Profile',
        },
        ASSET_MANAGER: {
            title: 'Asset Command Center',
            description: 'Track net worth, asset growth, and debt exposure with consistent analytics.',
            icon: PiggyBank,
            eyebrow: 'Asset Profile',
        },
        LIFE_MANAGER: {
            title: 'Life Command Center',
            description: 'Manage tasks, goals, and personal systems with a focused workflow view.',
            icon: HeartPulse,
            eyebrow: 'Life Profile',
        },
    };

    const activeProfile = profiles.find(p => p.id === activeProfileId) || null;
    const activeProfileType = profiles.find(p => p.id === activeProfileId)?.profile_type;
    const profileMeta = activeProfileType ? PROFILE_SHELL_META[activeProfileType] : null;
    const ProfileIcon = profileMeta?.icon;
    const useUnifiedProfileShell = Boolean(profileMeta);
    const filteredIncomeForTotal = income.filter(inc => !excludedCategories.includes(inc.category));
    const totalIncome = filteredIncomeForTotal.reduce((acc, item) => acc + item.amount, 0);
    const filteredExpensesForTotal = expenses.filter(exp => !excludedCategories.includes(exp.category));
    const totalExpenses = filteredExpensesForTotal.reduce((acc, item) => acc + item.amount, 0);
    const netIncome = totalIncome + totalExpenses;
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 30 }, (_, i) => (currentYear - i).toString());
    const months = [
        { name: "January", value: "01" }, { name: "February", value: "02" }, { name: "March", "value": "03" },
        { name: "April", value: "04" }, { name: "May", value: "05" }, { name: "June", value: "06" },
        { name: "July", value: "07" }, { name: "August", value: "08" }, { name: "September", value: "09" },
        { name: "October", value: "10" }, { name: "November", value: "11" }, { name: "December", value: "12" },
    ];

    // Transaction Details defaults to the same year as the dashboard-wide
    // "Select Year" control; its own text/month(s) filters (in ExpenseTable)
    // narrow further within that year.
    const filteredTransactions = allTransactions
        .filter(t => !excludedCategories.includes(t.category))
        .filter(t => selectedYears.includes(new Date(t.date).getFullYear().toString()))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

    const handleProfileSelect = (profileId) => {
        setActiveProfileId(profileId);
        setKey('profileDashboard'); // Switch to the profile dashboard tab
    };

    const handleTabSelect = (k) => {
        setKey(k);
        let activityType;
        switch (k) {
            case 'home':
                activityType = ActivityType.TAB_HOME_CLICKED;
                break;
            case 'profileDashboard':
                activityType = ActivityType.TAB_PROFILE_DASHBOARD_CLICKED;
                break;
            case 'manualEntry':
                activityType = ActivityType.TAB_RECORD_TRANSACTION_CLICKED;
                break;
            case 'recordAsset':
                activityType = ActivityType.TAB_RECORD_ASSET_CLICKED;
                break;
            case 'rules':
                activityType = ActivityType.TAB_RULES_CLICKED;
                break;
            default:
                activityType = ActivityType.TAB_CLICKED; // Fallback
        }
        logActivity(activityType, {}, activeProfileId);
    };

    const withProfilePanel = (content) => (
        useUnifiedProfileShell ? <div className="profile-shell-panel">{content}</div> : content
    );

    return (
        <div className="container-fluid">
            <Row>
                {showSidebar && (
                    <Col md={3} lg={2} className="sidebar-col">
                        <SideBar
                            profiles={profiles}
                            activeProfileId={activeProfileId}
                            onProfileSelect={handleProfileSelect}
                            onHomeSelect={() => setActiveProfileId(null)}
                            handleCreateProfile={handleCreateProfile}
                            handleUpdateProfile={handleUpdateProfile}
                            handleDeleteProfile={handleDeleteProfile}
                            onProfileVisibilityChange={handleProfileVisibilityChange}
                            showCreateProfileModalFromHome={showCreateProfileModalFromHome}
                            setShowCreateProfileModalFromHome={setShowCreateProfileModalFromHome}
                        />
                    </Col>
                )}
                <Col md={showSidebar ? 9 : 12} lg={showSidebar ? 10 : 12} className="main-content-col">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="sidebar-toggle-btn icon-only sidebar-toggle-floating"
                        onClick={() => setShowSidebar(prev => !prev)}
                        title={showSidebar ? 'Hide menu' : 'Show menu'}
                        aria-label={showSidebar ? 'Hide menu' : 'Show menu'}
                    >
                        {showSidebar ? <ChevronBarLeft size={18} /> : <ChevronBarRight size={18} />}
                    </Button>
                    <AppHeader
                        currentUser={currentUser}
                        onLogout={handleLogout}
                        onUpgradeClick={() => setShowSubscribeModal(true)}
                    />
                    {activeProfileId === null ? (
                        <HomePage onProfileSelect={handleProfileSelect} setShowCreateProfileModalFromHome={setShowCreateProfileModalFromHome} currentUser={currentUser} />
                    ) : (
                        <Tabs activeKey={key} onSelect={handleTabSelect} className="mb-3">
                            {currentUser?.is_premium ? (
                                activeProfileId && profileTypeLoaded && (
                                    <Tab eventKey="profileDashboard" title="Profile Dashboard">
                                    {activeProfileId ? (
                                        <div className={useUnifiedProfileShell ? 'profile-shell-root' : ''}>
                                            {useUnifiedProfileShell && (
                                                <div className="profile-shell-header-panel mb-4">
                                                    <div className="profile-shell-eyebrow">
                                                        {profileMeta.eyebrow}
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                                        <div>
                                                            <h2 className="profile-shell-title mb-1">
                                                                {ProfileIcon ? <ProfileIcon size={20} className="me-2" /> : null}
                                                                {profileMeta.title}
                                                            </h2>
                                                            <p className="profile-shell-subtitle mb-0">
                                                                {profileMeta.description}
                                                                {activeProfile?.name ? ` • ${activeProfile.name}` : ''}
                                                            </p>
                                                        </div>
                                                        {(activeProfileType === "EXPENSE_MANAGER" || activeProfileType === "ASSET_MANAGER") && (
                                                            <div className="profile-shell-filter-wrap">
                                                                <label htmlFor="yearSelectDashboard" className="form-label mb-1">Select Year(s)</label>
                                                                <select
                                                                    id="yearSelectDashboard"
                                                                    className="form-control"
                                                                    multiple
                                                                    value={selectedYears}
                                                                    onChange={handleSelectedYearsChange}
                                                                    title="Ctrl/Cmd-click to select multiple years"
                                                                >
                                                                    {years.map(year => (
                                                                        <option key={year} value={year}>{year}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {/* Year selector - only for EXPENSE_MANAGER and ASSET_MANAGER */}
                                            {!useUnifiedProfileShell && (activeProfileType === "EXPENSE_MANAGER" || activeProfileType === "ASSET_MANAGER") && (
                                                <div className="row">
                                                    <div className="col-lg-12 mb-4">
                                                        <div className="card">
                                                            <div className="card-body">
                                                                <div className="d-flex justify-content-end">
                                                                    <div className="col-md-3">
                                                                        <label htmlFor="yearSelectDashboard" className="form-label">Select Year(s):</label>
                                                                        <select
                                                                            id="yearSelectDashboard"
                                                                            className="form-control"
                                                                            multiple
                                                                            value={selectedYears}
                                                                            onChange={handleSelectedYearsChange}
                                                                            title="Ctrl/Cmd-click to select multiple years"
                                                                        >
                                                                            {years.map(year => (
                                                                                <option key={year} value={year}>{year}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {activeProfileType === "EXPENSE_MANAGER" && (
                                                <Tabs activeKey={dashboardSubTabKey} onSelect={(k) => {
                                                    setDashboardSubTabKey(k);
                                                    let subTabActivityType;
                                                    switch (k) {
                                                        case 'overview':
                                                            subTabActivityType = ActivityType.SUBTAB_OVERVIEW_CLICKED;
                                                            break;
                                                        case 'subcategoryTrends':
                                                            subTabActivityType = ActivityType.SUBTAB_SUBCATEGORY_TRENDS_CLICKED;
                                                            break;
                                                        case 'transactionDetails':
                                                            subTabActivityType = ActivityType.SUBTAB_TRANSACTION_DETAILS_CLICKED;
                                                            break;
                                                        case 'budget':
                                                            subTabActivityType = ActivityType.SUBTAB_BUDGET_CLICKED;
                                                            break;
                                                        default:
                                                            subTabActivityType = ActivityType.TAB_CLICKED; // Fallback
                                                    }
                                                    logActivity(subTabActivityType, {}, activeProfileId);
                                                }} className={`mb-3 ${useUnifiedProfileShell ? 'profile-shell-tabs' : ''}`}>
                                                    <Tab eventKey="overview" title={useUnifiedProfileShell ? 'Overview' : 'Overview'}>
                                                        {withProfilePanel(
                                                            <>
                                                        <div className="row">
                                                            <div className="col-lg-4">
                                                                <div className="card summary-card income-card mb-3">
                                                                    <div className="card-header">Total Income</div>
                                                                    <div className="card-body d-flex justify-content-between align-items-center">
                                                                        <h5 className="card-title mb-0">{formatCurrency(totalIncome, settings.currency)}</h5>
                                                                        <ArrowUpCircleFill size={32} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-4">
                                                                <div className="card summary-card expenses-card mb-3">
                                                                    <div className="card-header">Total Expenses</div>
                                                                    <div className="card-body d-flex justify-content-between align-items-center">
                                                                        <h5 className="card-title mb-0">{formatCurrency(totalExpenses, settings.currency)}</h5>
                                                                        <ArrowDownCircleFill size={32} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-4">
                                                                <div className="card summary-card net-income-card mb-3">
                                                                    <div className="card-header">Net Income</div>
                                                                    <div className="card-body d-flex justify-content-between align-items-center">
                                                                        <h5 className="card-title mb-0">{formatCurrency(netIncome, settings.currency)}</h5>
                                                                        <WalletFill size={32} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-lg-6">
                                                                <div className="card mb-4 shadow-lg">
                                                                    <div className="card-header">
                                                                        Expenses by Payment Source
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <PaymentSourcePieChart expenses={expenses} currency={settings.currency} paymentSources={paymentSources} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-lg-6">
                                                                <div className="card mb-4 shadow-lg">
                                                                    <div className="card-header">
                                                                        Payment Source Specific Monthly Overview
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="row mb-3">
                                                                            <div className="col-md-4">
                                                                                <select
                                                                                    className="form-control"
                                                                                    value={selectedPaymentSourceForChart}
                                                                                    onChange={e => setSelectedPaymentSourceForChart(e.target.value)}
                                                                                >
                                                                                    <option value="">All Payment Sources</option>
                                                                                    {paymentSources.map(source => (
                                                                                        <option key={source.source_name} value={source.source_name}>{source.source_name}</option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                        <PaymentSourceMonthlyBarChart
                                                                            income={income}
                                                                            expenses={expenses}
                                                                            selectedPaymentSource={selectedPaymentSourceForChart}
                                                                            currency={settings.currency}
                                                                            paymentSources={paymentSources}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row">
                                                            <div className="col-lg-12">
                                                                <div className="card mb-4 shadow-lg">
                                                                    <div className="card-header">
                                                                        Monthly Overview
                                                                    </div>
                                                                    <div className="card-body">
                                                                        <div className="row mb-3">
                                                                            <div className="col-md-6">
                                                                                <select
                                                                                    className="form-control"
                                                                                    value={selectedPaymentSourceForMonthlyTable}
                                                                                    onChange={e => setSelectedPaymentSourceForMonthlyTable(e.target.value)}
                                                                                >
                                                                                    <option value="">All Payment Sources</option>
                                                                                    {paymentSources.map(source => (
                                                                                        <option key={source.source_name} value={source.source_name}>{source.source_name}</option>
                                                                                    ))}
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                        <MonthlySummaryTable
                                                                            income={income}
                                                                            expenses={expenses}
                                                                            selectedPaymentSource={selectedPaymentSourceForMonthlyTable}
                                                                            currency={settings.currency}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row">
                                                            <div className="col-lg-12">
                                                                <div className="card mb-4 shadow-lg" style={{ position: 'relative', zIndex: 0 }}>
                                                                    <CategoryCostChart data={categoryCostData} budgets={settings.budgets} selectedYears={selectedYears} currency={settings.currency} />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="row mt-4">
                                                            <div className="col-lg-12">
                                                                <div className="card mb-4 shadow-lg" style={{ position: 'relative', zIndex: 1 }}>
                                                                    <MonthlyStackedBarChart data={monthlyCategoryExpenses} excludedCategories={excludedCategories} budgets={settings.budgets} currency={settings.currency} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                            </>
                                                        )}
                                                    </Tab>
                                                    <Tab eventKey="subcategoryTrends" title="Subcategory Trends">
                                                        {withProfilePanel(
                                                            <>
                                                            <div className="row mt-4">
                                                                <div className="col-lg-12">
                                                                    <CategorySubcategoryMonthlyCharts data={monthlyCategoryExpenses} excludedCategories={excludedCategories} budgets={settings.budgets} selectedYears={selectedYears} currency={settings.currency} activeProfileId={activeProfileId} />
                                                                </div>
                                                            </div>
                                                            </>
                                                        )}
                                                    </Tab>
                                                    <Tab eventKey="transactionDetails" title="Transaction Details">
                                                        {withProfilePanel(
                                                            <>
                                                            <ExpenseTable expenses={filteredTransactions} categories={settings.categories} onUpdateTransactionCategory={handleUpdateTransactionCategory} onAddRuleFromTransaction={handleAddRuleFromTransaction} currency={settings.currency} activeProfileId={activeProfileId} onDeleteTransaction={handleDeleteTransaction} onEditTransaction={handleUpdateTransaction} />
                                                            </>
                                                        )}
                                                    </Tab>
                                                    <Tab eventKey="budget" title="Budget">
                                                        {withProfilePanel(
                                                            <>
                                                            <BudgetVisualization settings={settings} categories={settings.categories} selectedYears={selectedYears} currency={settings.currency} activeProfileId={activeProfileId} />
                                                            </>
                                                        )}
                                                    </Tab>
                                                </Tabs>
                                            )}
                                            {activeProfileType === "ASSET_MANAGER" && (
                                                <div className={useUnifiedProfileShell ? 'profile-shell-panel' : ''}>
                                                    <AssetDashboard
                                                        assets={assets}
                                                        assetTypes={assetTypes}
                                                        currency={settings.currency}
                                                        activeProfileId={activeProfileId}
                                                        selectedYears={selectedYears}
                                                    />
                                                </div>
                                            )}
                                            {activeProfileType === "LIFE_MANAGER" && (
                                                <div className={useUnifiedProfileShell ? 'profile-shell-panel' : ''}>
                                                    <LifeManagementDashboard activeProfileId={activeProfileId} />
                                                </div>
                                            )}
                                            {activeProfileType === "MARKET_WATCH" && (
                                                <StockMarketDashboard profileId={activeProfileId} token={authService.getCurrentUser()?.access_token} />
                                            )}
                                        </div>
                                    ) : (
                                        <Card className="shadow-lg p-4 text-center mt-3">
                                            <Card.Body>
                                                <h2 className="text-primary mb-3">No Profile Selected</h2>
                                                <p className="lead mb-4">
                                                    Please select an existing profile from the sidebar or create a new one to view its dashboard.
                                                </p>
                                                <Button variant="primary" onClick={() => setShowCreateProfileModalFromHome(true)}>
                                                    <PlusCircle className="me-2" />Create New Profile
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    )}
                                </Tab>
                            )
                        ) : (
                            <Tab eventKey="profileDashboard" title={<span>Profile Dashboard <LockFill/></span>} disabled />
                        )}

                        {currentUser?.is_premium ? (
                            activeProfileId && profileTypeLoaded && activeProfileType === "EXPENSE_MANAGER" && (
                                <Tab eventKey="manualEntry" title="Record Transaction">
                                    <ManualTransactionEntry
                                        profileId={activeProfileId}
                                        paymentSources={paymentSources}
                                        onTransactionAdded={fetchData}
                                    />
                                </Tab>
                            )
                        ) : (
                            activeProfileId && profileTypeLoaded && activeProfileType === "EXPENSE_MANAGER" && (
                                <Tab eventKey="manualEntry" title={<span>Record Transaction <LockFill/></span>} disabled />
                            )
                        )}

                        {currentUser?.is_premium ? (
                            activeProfileId && profileTypeLoaded && activeProfileType === "ASSET_MANAGER" && (
                                <Tab eventKey="recordAsset" title="Record Asset">
                                    <RecordAsset
                                        profileId={activeProfileId}
                                        assetTypes={assetTypes}
                                        onAssetAdded={fetchData}
                                    />
                                </Tab>
                            )
                        ) : (
                            activeProfileId && profileTypeLoaded && activeProfileType === "ASSET_MANAGER" && (
                                <Tab eventKey="recordAsset" title={<span>Record Asset <LockFill/></span>} disabled />
                            )
                        )}

                        {currentUser?.is_premium ? (
                             activeProfileId && profileTypeLoaded && activeProfileType === "EXPENSE_MANAGER" && (
                                <Tab eventKey="rules" title="Rules">
                                    <RulesTab settings={settings} onSave={handleSaveSettings} categories={settings.categories} paymentSources={paymentSources} />
                                </Tab>
                            )
                        ) : (
                            activeProfileId && profileTypeLoaded && activeProfileType === "EXPENSE_MANAGER" && (
                                <Tab eventKey="rules" title={<span>Rules <LockFill/></span>} disabled />
                            )
                        )}

                        {currentUser?.is_premium ? (
                            activeProfileId && profileTypeLoaded && (activeProfileType === "EXPENSE_MANAGER" || activeProfileType === "ASSET_MANAGER") && (
                                <Tab eventKey="settings" title="Settings">
                                    <Settings
                                        settings={settings}
                                        onSave={handleSaveSettings}
                                        currency={settings.currency}
                                        profileId={activeProfileId}
                                        profileType={activeProfileType}
                                    />
                                </Tab>
                            )
                        ) : (
                            activeProfileId && profileTypeLoaded && (activeProfileType === "EXPENSE_MANAGER" || activeProfileType === "ASSET_MANAGER") && (
                                <Tab eventKey="settings" title={<span>Settings <LockFill/></span>} disabled />
                            )
                        )}
                    </Tabs>
                    )}
                    <SubscriptionModal
                        show={showSubscribeModal}
                        onHide={() => setShowSubscribeModal(false)}
                        onSubscribe={onSubscribe}
                    />
                </Col>
            </Row>
        </div>
    );
}


function App() {
    const [currentUser, setCurrentUser] = useState(null);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/users/me`);
            setCurrentUser(response.data);
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    useEffect(() => {
        if (authService.getCurrentUser()) {
            fetchUser();
        }
    }, []);

    const handleSubscribe = async (period) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/users/me/subscribe`, { period });
            setCurrentUser(response.data);
            alert('Subscription successful! You are now a Premium user.');
        } catch (error) {
            console.error('Error subscribing:', error);
            alert('Subscription failed. Please try again.');
        }
    };

    return (
        <Router>
            <Routes>
                <Route path="/login" element={<Login onLogin={fetchUser} />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/admin/*" element={<PrivateRoute><AdminPanel currentUser={currentUser} /></PrivateRoute>} />
                <Route path="/stock/:symbol" element={<PrivateRoute><StockDetail /></PrivateRoute>} />
                <Route path="/*" element={<PrivateRoute><MainApp currentUser={currentUser} onSubscribe={handleSubscribe} /></PrivateRoute>} />
            </Routes>
        </Router>
    );
}

export default App;
